/**
 * GlobalJsonInspectorDialog
 *
 * App-level mount of JsonStructureDetailsDialog driven by
 * `useJsonInspectorDialogStore`. Activated from grid context menus
 * ("JSON details" on column header / cell when the column is
 * declared-json) so the same Inspect-and-refresh UX runs outside the
 * MetaInfo manager.
 */

import { useCallback } from 'react';
import { JsonStructureDetailsDialog } from './JsonStructureDetailsDialog';
import { useJsonInspectorDialogStore } from './jsonInspectorDialogStore';
import { useMetaInfo, useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { removeJsonColumn } from '../../services/metaInfoApi';
import { openConfirmDialog } from '../confirm/confirmDialogStore';
import { toast } from '@/components/ui/toaster';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';

export function GlobalJsonInspectorDialog() {
  const { isOpen, context, closeDialog } = useJsonInspectorDialogStore();
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  const metaInfoQuery = useMetaInfo(
    context?.connectionId ?? null,
    context?.database ?? null,
  );

  const handleRemove = useCallback(() => {
    if (!context) return;
    const { connectionId, database, schema, table, column, source } = context;
    const decl = metaInfoQuery.data?.jsonColumns.find(
      (d) =>
        d.schema === schema && d.table === table && d.column === column,
    );
    if (!decl) {
      closeDialog();
      return;
    }
    const label = `${schema}.${table}.${column}`;
    openConfirmDialog({
      title: 'Remove JSON-column declaration?',
      description: `Remove the JSON-column declaration for ${label}? Filters and the tree-view cell renderer will fall back to plain-text behavior.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeJsonColumn(connectionId, database, decl.id);
        await invalidate(connectionId, database);
        const removed = useWorkspaceStore
          .getState()
          .removeJsonOrphanFilters({ connectionId, database, schema, table, column });
        if (removed > 0) {
          toast.success({
            title: `Removed ${removed} JSON filter${removed === 1 ? '' : 's'} on ${column}`,
          });
        }
        track('feature:json_undeclare', { source });
        closeDialog();
      },
    });
  }, [context, metaInfoQuery.data, invalidate, track, closeDialog]);

  if (!isOpen || !context) return null;

  const cache = metaInfoQuery.data?.jsonStructureCache.find(
    (c) =>
      c.schema === context.schema &&
      c.table === context.table &&
      c.column === context.column,
  );

  return (
    <JsonStructureDetailsDialog
      open
      onOpenChange={(o) => {
        if (!o) closeDialog();
      }}
      schema={context.schema}
      table={context.table}
      column={context.column}
      cache={cache ?? null}
      connectionId={context.connectionId}
      database={context.database}
      engine={context.engine}
      dataType={context.dataType}
      onRemoveDeclaration={handleRemove}
    />
  );
}
